import * as React from 'react';
import LocalDiv from '../../localization/LocalDiv';
import { Str } from '../../localization/translationKeys';
import { css } from '@emotion/core';
import { useInterval } from '../../core/hooks/useInterval';
import { TIME, diffMs } from '../../language/time';
import Metric from '../../layout/Metric';


const styles = {
  root:          css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-grow: 1;
  `,
  unitContainer: css`
    display: flex;
    flex-direction: column;
  `,
  value:         css`
    font-size: 30px;
    font-weight: bold;
    text-align: center;
  `,
  unit:          css`
    font-size: 11px;
    text-align: center;
  `,
};

type Props = {
  deadline: Date;
  onTick?: (diff: number) => void;
  className?: string;
};

const pad   = (time: string): string => time.length === 1 ? `0${time}` : time;
const toStr = (time: number): string => pad(`${Math.max(0, Math.floor(time))}`);

/**
 * CountDown: a component that renders a countdown from a target datetime in days, hours, minutes
 *
 * PureComponent: true
 * Redux Connected: false
 */
const CountDown: React.FC<Props> = props => {
  const [remainingMs, setRemainingMs] = React.useState<number>(props.deadline.getTime() - new Date().getTime());

  const callback = React.useCallback(
    () => {
      const diff = diffMs(props.deadline, new Date());
      props.onTick?.(diff);
      setRemainingMs(diff);
    },
    [props.deadline, props.onTick],
  );

  useInterval(callback, 10 * TIME.S);

  const remainingDays    = React.useMemo(() => toStr(remainingMs / TIME.D), [remainingMs]);
  const remainingHours   = React.useMemo(() => toStr(remainingMs / TIME.H % 24), [remainingMs]);
  const remainingMinutes = React.useMemo(() => toStr(remainingMs / TIME.M % 60), [remainingMs]);

  return (
    <LocalDiv css={styles.root} className={props.className}>
      <Metric value={remainingDays} label={Str.Days} defaultLabel='Days' />
      <Metric value={remainingHours} label={Str.Hours} defaultLabel='Hours' />
      <Metric value={remainingMinutes} label={Str.Minutes} defaultLabel='Minutes' />
    </LocalDiv>
  );
};

export default CountDown;
